/**
 * IdleWarningModal — the "You'll be signed out in 60 seconds" prompt.
 * Rendered by <Layout> so it lives on every authenticated page. Driven
 * entirely by the useIdleWatcher hook (see hooks/useIdleWatcher.js).
 *
 * Uses a shadcn <Dialog> with modal={true} so keyboard focus is trapped
 * and clicks on the backdrop do NOT dismiss — the user must explicitly
 * pick Stay or Sign out. Any keyboard/mouse event on the app already
 * dismisses via the hook's activity listeners, so the "silently ignore
 * backdrop" behaviour is safe.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle } from 'lucide-react';

export function IdleWarningModal({ open, secondsRemaining, onStay, onSignOut }) {
  return (
    <Dialog open={open} onOpenChange={() => { /* intentional no-op — user must pick */ }}>
      <DialogContent
        className="sm:max-w-md"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        data-testid="idle-warning-modal"
      >
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-amber-100">
            <AlertTriangle className="h-6 w-6 text-amber-600" aria-hidden />
          </div>
          <DialogTitle className="text-center">You&apos;re about to be signed out</DialogTitle>
          <DialogDescription className="text-center">
            For your security we sign you out after a period of inactivity. You&apos;ll be
            signed out in{' '}
            <span className="font-semibold text-foreground" data-testid="idle-warning-seconds">
              {secondsRemaining ?? '—'}
            </span>
            {' '}second{secondsRemaining === 1 ? '' : 's'}.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-center">
          <Button variant="outline" onClick={onSignOut} data-testid="idle-signout-btn">
            Sign out now
          </Button>
          <Button onClick={onStay} data-testid="idle-stay-btn" autoFocus>
            Stay signed in
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
